import {
  colorThemeProperties,
  type dbStructureGenerationModels,
  fontFamilyValues,
  radiusThemeProperties,
} from '@features/db/schema';
import {mapObjectValues} from '@features/utils/object';
import {z} from 'zod';

// Full font key set — includes components/Taste-only fonts. Drives FontKey and
// the components Assets font picker. Legacy themes use the narrower db enum
// (fontFamilyValues) instead.
export const fontKeys = [
  // Curated Dinamo set used by the taste-engine POC
  'abcDiatype',
  'abcDiatypeRounded',
  'abcFavorit',
  'abcFavoritMono',
  'abcGramercy',
  'abcGravity',
  'abcMarfaMono',
  'abcMarist',
  'abcOtto',
  'abcSocial',
  'abcSolar',
  'abcWhyte',
  // Non-ABC display faces bundled for Taste flows
  'futuraPt',
  'fonseca',
  'novaDeco',
  // Legacy Google fonts kept so existing themes still validate
  'inter',
  'dmSans',
  'nunito',
  'ptSerif',
  'spaceGrotesk',
  'ibmPlexMono',
  'ibmPlexSans',
  'robotoSlab',
  'geist',
  'shantellSans',
] as const;

export type FontKey = (typeof fontKeys)[number];

export const defaultStructureGenerationModel = 'auto';

export type StructureGenerationModel =
  (typeof dbStructureGenerationModels)[number];

const getRadiusSchema = ({size, example}: {size: string; example: number}) =>
  z.string().describe(`${size} border radius (in px, default "${example}px")`);
const radiusSchema = z.object(
  mapObjectValues(radiusThemeProperties, getRadiusSchema),
);

export const rgbSchemaPrefix = '#ABC123 hex code format. ';
const getRgbSchema = (description: string = '') =>
  z.string().describe(`${rgbSchemaPrefix}${description}`);

export const colorSchema = z.object(
  mapObjectValues(colorThemeProperties, getRgbSchema),
);

export const designTokensSchema = z.object({
  ...colorSchema.shape,
  ...radiusSchema.shape,

  // Legacy themes store to the db font enum, so validate against that subset
  // (not FontKey, which includes components-only Taste fonts).
  fontFamilyBody: z
    .enum(fontFamilyValues)
    .describe('Font family for all text elements on page'),
});

export type ThemeRadiusProps = z.infer<typeof radiusSchema>;

export const claudeHtmlCssResponseSchema = z.object({
  screenName: z.string().nullable(),
  html: z.string().min(1, 'HTML is required'),
});

const createThemeActionSchema = z.object({
  action: z.literal('create'),
  themeName: z.string(),
  designTokens: designTokensSchema,
});
export const createThemeDecisionSchema = z.object({
  themeDecision: createThemeActionSchema,
});

const notNeededThemeActionSchema = z.object({
  action: z.literal('not_needed'),
});

export const themeDecisionSchema = z.object({
  themeDecision: z.union([notNeededThemeActionSchema, createThemeActionSchema]),
});

export const webHtmlCssChatApiRequestSchema = z.object({
  flowId: z.string().min(1, 'Flow ID is required'),
  screenId: z.string().min(1, 'Chat ID is required'),
  abortToken: z.string().optional(),
});

export type HtmlCssStructureApiRequest = z.infer<
  typeof webHtmlCssChatApiRequestSchema
>;
